import { onMounted, ref, watch } from 'vue'
import { debounce } from '~/assets/js/helpers'
import { storeToRefs } from 'pinia'

export function useLayoutSetup() {
  const route = useRoute()
  const { $track, $i18n, $sentry } = useNuxtApp()
  const userStore = useUserStore()
  const { email: storedEmail } = storeToRefs(userStore)
  const { storeCurrency } = useStorefront()
  const pageStore = usePageStore()
  const modalStore = useModalStore()
  const shopifyStore = useShopifyStore()
  const { preventBodyScroll, cookieStatus } = storeToRefs(pageStore)
  const scrollTop = ref(0)
  const pageLoaded = ref(false)
  const pageHeight = ref(0)

  function getEmail() {
    // Try to get email from emailAd query parameter
    let email = route.query.emailAd

    // Try to get email from _ke query parameter
    if (!email && route.query._ke) {
      try {
        email = JSON.parse(
          Buffer.from(route.query._ke, 'base64').toString()
        ).kl_email
      } catch (error) {
        console.error(error)
        $sentry.captureException(error)
      }
    }

    // Store the email if we've got it
    if (email) {
      userStore.setUserEmail(email)
      $track.updateUserData()
    }
  }

  function storeGoogleClickId () {
    // Try to get gclid from query parameter
    const gclid = route.query.gclid
    if (gclid) {
      userStore.setGoogleClickID(gclid)
    }
  }

  function identify() {
    // Klaviyo
    const _learnq = window._learnq || []
    _learnq.push([
      'identify',
      {
        $email: storedEmail.value,
        language: $i18n.locale.value,
        currency: storeCurrency.value,
      },
    ])

    // Rudderstack
    if (storedEmail.value) {
      $track.identify(storedEmail.value, {
        language: $i18n.locale.value,
        currency: storeCurrency.value,
      })
    }
  }

  watch(storedEmail, (newValue) => {
    if (newValue) {
      identify()
    }
  })

  async function applyCodeFromURL() {
    const details = getPromoDetailsFromURL()
    if (details?.code || route.query?.code) {
      await shopifyStore.applyCouponCode({
        code: details?.code || route.query.code,
        silent: true
      })
    }
  }

  function getPromoDetailsFromURL() {
    const encodedDetails = route.query.d
    if (!encodedDetails) return

    let details

    try {
      details = JSON.parse(atob(encodedDetails))
    } catch (error) {
      console.error(error)
      $sentry.captureException(error)
      return
    }

    if (route.query.applyCode) details.code = route.query.applyCode

    return details
  }

  function updateHTMLClasses(value) {
    const htmlElem = document.querySelector('html')
    const bodyElem = document.querySelector('body')

    if (value) {
      htmlElem.style.height = '100vh'
      htmlElem.style.scrollBehavior = 'auto'
      bodyElem.style.position = 'fixed'
      bodyElem.style.overflowY = 'hidden'
      bodyElem.style.top = `-${scrollTop.value}px`
      bodyElem.style.width = '100%'
    } else {
      const scrollY = bodyElem.style.top
      htmlElem.style.height = ''
      bodyElem.style.position = ''
      bodyElem.style.top = ''
      bodyElem.style.overflowY = ''
      bodyElem.style.width = ''
      window.scrollTo(0, parseInt(scrollY || '0') * -1)
      htmlElem.style.scrollBehavior = ''
    }
  }

  function checkLoadState() {
    if (document.readyState === 'complete') pageLoaded.value = true
  }

  function openEmailModal() {
    checkLoadState()

    const canShowEmailModal = () => {
      const allowedRoutePattern = /^locale$|^locale-shop-category|^slp-|^lp-/

      if (!pageStore.cookiesAllowed) return false // Don't show until cookies have been accepted
      if (pageStore.emailModalShown) return false // Don't show multiple times
      if (storedEmail.value) return false // Don't show if we've already got their email address
      if (!pageLoaded.value) return false // Don't show until the page has fully loaded
      if (!route.name?.match(allowedRoutePattern)) return false // Only show on homepage, PLPs, sports LPs and normal LPs
      return true
    }

    if (canShowEmailModal()) {
      modalStore.OPEN_MODAL_PAGE({ slug: 'email-signup' })
      pageStore.updateEmailModalStatus(true)
    }
  }

  onMounted(async () => {
    userStore.setUUID()

    if (shopifyStore.cartId) {
      await shopifyStore.getCart()
    }

    setTimeout(async () => {
      try {
        getEmail()
        identify()
        applyCodeFromURL()
        storeGoogleClickId()
      } catch (error) {
        $sentry.captureException(error)
      }

      pageHeight.value = document.documentElement.getBoundingClientRect().height

      setTimeout(() => {
        openEmailModal()
      }, 8000)
    })

    if (preventBodyScroll.value) {
      updateHTMLClasses(preventBodyScroll.value)
    }

    window.addEventListener(
      'scroll',
      debounce(() => {
        scrollTop.value = window.scrollY
      }),
      200
    )

    window.addEventListener('load', checkLoadState)

    // Update page history
    const { path, params, meta, name } = route
    pageStore.updatePageHistory({
      path,
      name,
      label: meta.title || params.category,
    })

    // Store this in sessionStorage so it can be accessed before the page is initialised
    try {
      sessionStorage.setItem('pageHistory', JSON.stringify(pageStore.pageHistory))
    } catch (error) {
      $sentry.captureException(error)
    }

    if (route.query.code || route.query.applyCode) {
      localStorage.setItem('emailCapturePopup', 'true')
    }

    // Update google ads consent - this may have already been done but we don't want to risk
    // not having consent logged
    updateGoogleAdsConsent()
  })

  watch(preventBodyScroll, (newValue) => {
    updateHTMLClasses(newValue)
  })

  watch(cookieStatus, () => {
    updateGoogleAdsConsent()
  })
  
  if (import.meta.client) {
    window.dataLayer = window.dataLayer || []
    function gtag() { window.dataLayer.push(arguments) }
    
    gtag('consent', 'default', {
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied',
      'analytics_storage': 'denied'
    })
  }
  
  async function updateGoogleAdsConsent () {
    if (!window?.gtag) return
    const state = cookieStatus.value ? 'granted' : 'denied'
    window.gtag('consent', 'update', {
      ad_storage: state,
      analytics_storage: state,
      ad_user_data: state,
      ad_personalization: state
    })
  }
  if (import.meta.client && cookieStatus.value) updateGoogleAdsConsent()
}
